import { useState, useRef, useEffect} from 'react'
import { FRANCE_REGIONS, FRANCE_DEPARTEMENTS } from './franceMapData'
import { CP_CENTROIDS } from './cpCentroids'

const AURA_REG_CODE = '84'

function MapContent({ svgRef, transform, setTransform, features, granularite, geoData, depRef, cpRef, total, dragging, setDragging, setDragStart, dragStart, tooltip, setTooltip, cpBubbles }) {

  function isLocalFeature(code) {
    if (granularite === 'region') return code === AURA_REG_CODE
    return code === depRef
  }

  function getCount(code) { return geoData[code] || 0 }

  function getColor(code) {
    if (isLocalFeature(code)) return '#e67e22'
    const count = getCount(code)
    if (count === 0) return '#eaf0ed'
    const max = Math.max(...Object.values(geoData), 1)
    const intensity = count / max
    const r = Math.round(26 + (52 - 26) * (1 - intensity))
    const g = Math.round(122 + (186 - 122) * (1 - intensity))
    const b = Math.round(94 + (150 - 94) * (1 - intensity))
    return 'rgb(' + r + ',' + g + ',' + b + ')'
  }

  const lastDist = useRef(null)

  const onMouseDown = e => {
    setDragging(true)
    setDragStart({ x: e.clientX - transform.x, y: e.clientY - transform.y })
  }
  const onMouseMove = e => {
    if (!dragging || !dragStart) return
    setTransform(prev => ({ ...prev, x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }))
  }
  const onMouseUp = () => { setDragging(false); setDragStart(null) }

  const onTouchStart = e => {
    if (e.touches.length === 1) {
      setDragging(true)
      setDragStart({ x: e.touches[0].clientX - transform.x, y: e.touches[0].clientY - transform.y })
    }
  }
  const onTouchMove = e => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX
      const dy = e.touches[0].clientY - e.touches[1].clientY
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (lastDist.current) {
        const delta = dist / lastDist.current
        setTransform(prev => ({ ...prev, scale: Math.min(Math.max(prev.scale * delta, 0.8), 14) }))
      }
      lastDist.current = dist
    } else if (e.touches.length === 1 && dragging && dragStart) {
      setTransform(prev => ({ ...prev, x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y }))
    }
  }
  const onTouchEnd = () => { lastDist.current = null; setDragging(false) }

  // Attacher wheel avec passive:false sur le SVG lui-meme
  useEffect(() => {
    const el = svgRef.current
    if (!el) return
    const handler = e => {
      e.preventDefault()
      const rect = el.getBoundingClientRect()
      const mouseX = (e.clientX - rect.left) / rect.width * 500
      const mouseY = (e.clientY - rect.top) / rect.height * 520
      setTransform(prev => {
        const delta = e.deltaY > 0 ? 0.85 : 1.18
        const newScale = Math.min(Math.max(prev.scale * delta, 0.8), 14)
        const x = mouseX - (mouseX - prev.x) * (newScale / prev.scale)
        const y = mouseY - (mouseY - prev.y) * (newScale / prev.scale)
        return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, scale: Math.round(newScale * 1000) / 1000 }
      })
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [svgRef, setTransform])

  return (
    <svg
      ref={svgRef}
      width="100%" height="100%" viewBox="0 0 500 520"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{ userSelect: 'none', cursor: dragging ? 'grabbing' : 'grab' }}
    >
      <g transform={'translate(' + transform.x + ',' + transform.y + ') scale(' + transform.scale + ')'}>
        {features.map(feat => (
          <path
            key={feat.code}
            d={feat.path}
            fill={granularite === 'cp' ? '#e8efeb' : getColor(feat.code)}
            stroke="#8aaa96"
            strokeWidth={(granularite === 'region' ? 1.2 : 0.6) / transform.scale}
            onMouseEnter={granularite !== 'cp'
              ? e => setTooltip({ nom: feat.nom, count: getCount(feat.code), x: e.clientX, y: e.clientY })
              : undefined}
            onMouseLeave={granularite !== 'cp' ? () => setTooltip(null) : undefined}
            style={{ transition: 'fill 0.2s' }}
          />
        ))}
        {granularite === 'cp' && cpBubbles
          .sort((a, b) => b.baseR - a.baseR)
          .map(b => {
            const r = b.baseR / transform.scale
            return (
              <g key={b.cp}>
                <circle
                  cx={b.x} cy={b.y} r={r}
                  fill={b.isRef ? '#e67e22' : '#1a7a5e'}
                  fillOpacity={0.78}
                  stroke="white"
                  strokeWidth={1 / transform.scale}
                  onMouseEnter={e => setTooltip({ nom: 'CP ' + b.cp, count: b.count, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip(null)}
                />
                {b.baseR > 10 && (
                  <text x={b.x} y={b.y} textAnchor="middle" dominantBaseline="middle"
                    fontSize={Math.min(9, b.baseR * 0.65) / transform.scale}
                    fill="white" fontWeight="bold" style={{ pointerEvents: 'none' }}>
                    {b.count}
                  </text>
                )}
              </g>
            )
          })
        }
      </g>
    </svg>
  )
}

export default function FranceMap({ granularite, geoData, depRef, cpRef, total }) {
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 })
  const [transformFs, setTransformFs] = useState({ x: 0, y: 0, scale: 1 })
  const [dragging, setDragging] = useState(false)
  const [draggingFs, setDraggingFs] = useState(false)
  const [dragStart, setDragStart] = useState(null)
  const [dragStartFs, setDragStartFs] = useState(null)
  const [tooltip, setTooltip] = useState(null)
  const [fullscreen, setFullscreen] = useState(false)
  const svgRef = useRef(null)
  const svgFsRef = useRef(null)

  const features = granularite === 'region' ? FRANCE_REGIONS : FRANCE_DEPARTEMENTS

  function getCPBubbles() {
    const entries = Object.entries(geoData)
    if (entries.length === 0) return []
    const maxCount = Math.max(...entries.map(e => e[1]), 1)
    return entries.map(([cp, count]) => {
      const centroid = CP_CENTROIDS[cp]
      if (!centroid) return null
      const baseR = Math.max(5, Math.sqrt(count / maxCount) * 24)
      return { cp, count, x: centroid.x, y: centroid.y, baseR, isRef: cp === cpRef }
    }).filter(Boolean)
  }

  const cpBubbles = granularite === 'cp' ? getCPBubbles() : []

  function resetZoom() { setTransform({ x: 0, y: 0, scale: 1 }) }
  function resetZoomFs() { setTransformFs({ x: 0, y: 0, scale: 1 }) }

  const Legend = () => granularite === 'cp' ? (
    <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(255,255,255,0.92)', borderRadius: 6, padding: '4px 10px', fontSize: 11, display: 'flex', gap: 8, alignItems: 'center' }}>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#e67e22', display: 'inline-block' }}/><span>CP {cpRef}</span>
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#1a7a5e', display: 'inline-block', marginLeft: 6 }}/><span>Autres</span>
    </div>
  ) : null

  const TooltipEl = () => tooltip ? (
    <div style={{ position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 36, background: 'rgba(0,0,0,0.82)', color: 'white', padding: '5px 10px', borderRadius: 5, fontSize: 12, pointerEvents: 'none', zIndex: 3000, whiteSpace: 'nowrap' }}>
      {tooltip.nom} : <strong>{tooltip.count}</strong> coureur{tooltip.count > 1 ? 's' : ''}
      {total > 0 ? ' (' + Math.round(tooltip.count / total * 100) + '%)' : ''}
    </div>
  ) : null

  return (
    <>
      {/* VUE NORMALE */}
      <div style={{ position: 'relative', width: '100%', height: '340px', overflow: 'hidden', borderRadius: 8, background: '#f0f4f2' }}>
        <MapContent
          svgRef={svgRef}
          transform={transform}
          setTransform={setTransform}
          features={features}
          granularite={granularite}
          geoData={geoData}
          depRef={depRef}
          cpRef={cpRef}
          total={total}
          dragging={dragging}
          setDragging={setDragging}
          dragStart={dragStart}
          setDragStart={setDragStart}
          tooltip={tooltip}
          setTooltip={setTooltip}
          cpBubbles={cpBubbles}
        />
        <Legend />
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6 }}>
          <button onClick={() => { setFullscreen(true); setTransformFs({ x: 0, y: 0, scale: 1 }) }}
            style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            Plein ecran
          </button>
          <button onClick={resetZoom}
            style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', fontSize: 12, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            Reset
          </button>
        </div>
        <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: '#666' }}>
          Scroll pour zoomer
        </div>
        <TooltipEl />
      </div>

      {/* PLEIN ECRAN */}
      {fullscreen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '90vw', height: '90vh', background: '#f0f4f2', borderRadius: 12, position: 'relative', overflow: 'hidden' }}>
            <MapContent
              svgRef={svgFsRef}
              transform={transformFs}
              setTransform={setTransformFs}
              features={features}
              granularite={granularite}
              geoData={geoData}
              depRef={depRef}
              cpRef={cpRef}
              total={total}
              dragging={draggingFs}
              setDragging={setDraggingFs}
              dragStart={dragStartFs}
              setDragStart={setDragStartFs}
              tooltip={tooltip}
              setTooltip={setTooltip}
              cpBubbles={cpBubbles}
            />
            <Legend />
            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 6 }}>
              <button onClick={resetZoomFs}
                style={{ background: 'white', border: '1px solid #ddd', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }}>
                Reset
              </button>
              <button onClick={() => setFullscreen(false)}
                style={{ background: '#1a7a5e', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                Fermer
              </button>
            </div>
            <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(255,255,255,0.9)', borderRadius: 6, padding: '4px 8px', fontSize: 11, color: '#666' }}>
              Scroll pour zoomer
            </div>
            <TooltipEl />
          </div>
        </div>
      )}
    </>
  )
}
